import type { NodeInstance } from 'models/nodes/NodeInstance';
import { parseConsultationOutput } from 'models/appointments';
import AppointmentActionsPanel from './appointments/AppointmentActionsPanel';

export const COMPLETED_CONSULTATION_HEADING = 'Consultation completed';

const formatDateTime = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

const prettyJson = (raw?: string): string => {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

type Props = {
  /** The completed Consultation node whose outputJson holds the appointment. */
  node: NodeInstance;
  /** Owning team — carried as a route param on the rule-bypass team endpoints. */
  teamId: string;
  /** Human-readable customer name (the detail DTO carries only a userId). */
  customerDisplay?: string;
  /** Awaited after a team cancel/reschedule so the host re-fetches the instance;
   *  the refetched outputJson stays the display source of truth (plan Decision 5).
   *  When absent the actions stay hidden — there is no safe refresh path. */
  onRefresh?: () => Promise<void> | void;
};

/**
 * Team/operations view of a completed Consultation node, rendered inside
 * WorkflowInstanceView's completed-node chain. Parses the node outputJson via
 * the casing-tolerant `parseConsultationOutput` (M2) and renders the summary
 * (customer, time, location/meeting link) plus the shared
 * `AppointmentActionsPanel` in team scope — all mutations flow through
 * `useAppointmentActions`, never direct ApiService calls. Unparseable output
 * falls back to the raw JSON block; a missing submissionId hides the actions
 * entirely (Req 7 graceful degradation).
 */
export default function CompletedConsultationPanel({
  node,
  teamId,
  customerDisplay,
  onRefresh,
}: Props) {
  const appointment = parseConsultationOutput(node.outputJson);

  if (!appointment) {
    return (
      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono">
        {prettyJson(node.outputJson)}
      </pre>
    );
  }

  return (
    <div className="text-sm text-gray-700 space-y-1">
      <p className="font-medium">{COMPLETED_CONSULTATION_HEADING}</p>
      {customerDisplay && <p>Customer: {customerDisplay}</p>}
      {appointment.appointmentTimeUtc && (
        <p>Time: {formatDateTime(appointment.appointmentTimeUtc)}</p>
      )}
      {appointment.address && <p>Location: {appointment.address}</p>}
      {appointment.meetingUrl && <p>Meeting link: {appointment.meetingUrl}</p>}
      {onRefresh && (
        <AppointmentActionsPanel
          appointment={appointment}
          scope="team"
          teamId={teamId}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}
